import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  Clock,
  Copy,
  Inbox,
  RotateCcw,
  X,
} from "lucide-react";
import { track } from "../analytics";
import type { CaptureRecord, Placement, ZoneId } from "./challengeData";
import {
  FRAGMENTS,
  FRAGMENT_MS,
  QUESTIONS,
  RECALL_MS,
  ZONES,
  scoreRun,
  shareLine,
  verdictFor,
} from "./challengeData";

/**
 * The playable half of /challenge.
 *
 * The whole component renders its "intro" phase with no timers, no window
 * reads and no randomness, so the prerendered HTML and the first client render
 * are identical — every clock starts on a user gesture, never on mount.
 */

type Phase = "intro" | "capture" | "handoff" | "recall" | "result";

const ZONE_TINT: Record<ZoneId, { chip: string; panel: string; text: string }> = {
  decisions: {
    chip: "border-indigo-200 bg-indigo-50 hover:bg-indigo-100",
    panel: "border-indigo-200 bg-indigo-50/60",
    text: "text-indigo-900",
  },
  owners: {
    chip: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100",
    panel: "border-emerald-200 bg-emerald-50/60",
    text: "text-emerald-900",
  },
  dates: {
    chip: "border-amber-200 bg-amber-50 hover:bg-amber-100",
    panel: "border-amber-200 bg-amber-50/60",
    text: "text-amber-900",
  },
  risks: {
    chip: "border-rose-200 bg-rose-50 hover:bg-rose-100",
    panel: "border-rose-200 bg-rose-50/60",
    text: "text-rose-900",
  },
};

function fragmentText(id: number): string {
  return FRAGMENTS.find((f) => f.id === id)?.text ?? "";
}

/**
 * What the player has to search during the recall round.
 *
 * This is the argument the whole game makes, so it is not rigged: placed lines
 * sit under the header the player chose (including the wrong one, if they
 * mis-filed it) and dumped lines sit in one undifferentiated scroll box in
 * reverse order, exactly the way a linear notes app hands them back. Nothing
 * is hidden and nothing is padded — the only difference is structure.
 */
function NotesPanel({ records }: { records: CaptureRecord[] }) {
  const dumped = records.filter((r) => r.placement === "dump");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">Your notes</h3>

      <div className="space-y-3">
        {ZONES.map((zone) => {
          const items = records.filter((r) => r.placement === zone.id);
          if (items.length === 0) return null;
          const tint = ZONE_TINT[zone.id];
          return (
            <div key={zone.id} className={`rounded-xl border p-3 ${tint.panel}`}>
              <p className={`mb-1.5 text-xs font-bold uppercase tracking-wide ${tint.text}`}>
                {zone.label}
              </p>
              <ul className="space-y-1">
                {items.map((r) => (
                  <li key={r.fragmentId} className="text-sm leading-snug text-slate-700">
                    {fragmentText(r.fragmentId)}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {dumped.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
              <Inbox size={12} aria-hidden="true" />
              Notes ({dumped.length})
            </p>
            {/* Capped height on purpose: the pile is only realistic if you
                have to scroll it under a clock. */}
            <ul className="max-h-44 space-y-1 overflow-y-auto pr-1">
              {[...dumped].reverse().map((r) => (
                <li key={r.fragmentId} className="text-[13px] leading-snug text-slate-600">
                  {fragmentText(r.fragmentId)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {records.length === 0 && (
          <p className="text-sm text-slate-600">
            You didn&apos;t capture anything. That happens too.
          </p>
        )}
      </div>
    </div>
  );
}

export function RecallChallenge() {
  const [phase, setPhase] = useState<Phase>("intro");
  // index and records move together in one pure updater. Held apart, two
  // keypresses landing in the same frame both read the same stale `index`:
  // one fragment gets filed twice and the next one is skipped without ever
  // being shown. Under time pressure that is not a rare input pattern.
  const [capture, setCapture] = useState<{
    index: number;
    records: CaptureRecord[];
  }>({ index: 0, records: [] });
  const { index, records } = capture;
  const [fragmentProgress, setFragmentProgress] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    QUESTIONS.map(() => null)
  );
  const [recallLeft, setRecallLeft] = useState(RECALL_MS);
  const [copied, setCopied] = useState(false);

  const score = useMemo(() => scoreRun(records, answers), [records, answers]);

  const advance = useCallback((placement: Placement | null) => {
    setCapture((prev) => {
      if (prev.index >= FRAGMENTS.length) return prev;
      return {
        index: prev.index + 1,
        records:
          placement === null
            ? prev.records
            : [
                ...prev.records,
                { fragmentId: FRAGMENTS[prev.index].id, placement },
              ],
      };
    });
  }, []);

  // Capture round: one clock per fragment. Miss it and the line is gone —
  // from the pile and from the clusters alike, which is what makes filing
  // cost something.
  useEffect(() => {
    if (phase !== "capture" || index >= FRAGMENTS.length) return;
    const started =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    setFragmentProgress(0);
    const tick = window.setInterval(() => {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      setFragmentProgress(Math.min(1, (now - started) / FRAGMENT_MS));
    }, 60);
    const miss = window.setTimeout(() => advance(null), FRAGMENT_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(miss);
    };
  }, [phase, index, advance]);

  useEffect(() => {
    if (phase !== "capture" || index < FRAGMENTS.length) return;
    setPhase("handoff");
  }, [phase, index]);

  useEffect(() => {
    if (phase !== "handoff") return;
    const placed = records.filter((r) => r.placement !== "dump").length;
    track("challenge_capture_done", {
      captured: records.length,
      total: FRAGMENTS.length,
      placed,
      dumped: records.length - placed,
    });
  }, [phase, records]);

  // Keyboard is the fast path, and it matters that both paths cost the same
  // number of keystrokes: the price of placing is reading the line and
  // deciding, not an extra click we invented.
  useEffect(() => {
    if (phase !== "capture") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === " " || e.key.toLowerCase() === "d") {
        e.preventDefault();
        advance("dump");
        return;
      }
      const zone = ZONES.find((z) => z.key === e.key);
      if (zone) {
        e.preventDefault();
        advance(zone.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, advance]);

  // Recall round: one clock for all five questions, so answering fast buys
  // time for the one you have to hunt for.
  useEffect(() => {
    if (phase !== "recall") return;
    const started =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    setRecallLeft(RECALL_MS);
    const tick = window.setInterval(() => {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      setRecallLeft(Math.max(0, RECALL_MS - (now - started)));
    }, 100);
    const end = window.setTimeout(() => setPhase("result"), RECALL_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(end);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "result") return;
    track("challenge_complete", {
      captured: score.captured,
      total: score.total,
      placed: score.placed,
      dumped: score.dumped,
      recalled: score.recalled,
      questions: score.questions,
    });
  }, [phase, score]);

  const start = () => {
    track("challenge_start");
    setPhase("capture");
  };

  const answerQuestion = (choice: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[qIndex] = choice;
      return next;
    });
    if (qIndex + 1 >= QUESTIONS.length) {
      setPhase("result");
    } else {
      setQIndex((i) => i + 1);
    }
  };

  const replay = () => {
    track("challenge_replay");
    setCapture({ index: 0, records: [] });
    setQIndex(0);
    setAnswers(QUESTIONS.map(() => null));
    setFragmentProgress(0);
    setRecallLeft(RECALL_MS);
    setCopied(false);
    setPhase("capture");
  };

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(shareLine(score));
      setCopied(true);
      track("challenge_share_copied", {
        captured: score.captured,
        recalled: score.recalled,
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission can be denied; the line is selectable on screen.
      setCopied(false);
    }
  };

  // ── Intro (this is the prerendered state) ──────────────────────
  if (phase === "intro") {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-600">
          About a minute · no signup
        </p>
        <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Sixteen lines from a real kickoff meeting. Then five questions.
        </h2>
        <div className="mb-6 space-y-3 text-slate-600">
          <p>
            Each line lands for {(FRAGMENT_MS / 1000).toFixed(1)} seconds. You can{" "}
            <strong className="text-slate-900">dump it</strong> — one key, no
            thinking — or <strong className="text-slate-900">place it</strong>{" "}
            under Decisions, Owners, Dates or Risks, which costs you the moment
            it takes to read and decide. Miss the clock and the line is gone.
          </p>
          <p>
            Then the meeting is over and someone asks you five questions, with
            25 seconds on the clock and only the notes you kept.
          </p>
        </div>
        <button
          type="button"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
        >
          Start the meeting
          <ArrowRight size={16} aria-hidden="true" />
        </button>
        <p className="mt-3 text-sm text-slate-600">
          Keyboard: <kbd className="rounded border border-slate-300 px-1.5 py-0.5 text-xs font-semibold">space</kbd>{" "}
          to dump, <kbd className="rounded border border-slate-300 px-1.5 py-0.5 text-xs font-semibold">1</kbd>–
          <kbd className="rounded border border-slate-300 px-1.5 py-0.5 text-xs font-semibold">4</kbd> to place.
        </p>
      </div>
    );
  }

  // ── Capture round ──────────────────────────────────────────────
  if (phase === "capture") {
    const fragment = FRAGMENTS[Math.min(index, FRAGMENTS.length - 1)];
    const placed = records.filter((r) => r.placement !== "dump").length;
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-4 flex items-center justify-between text-sm font-medium text-slate-600">
          <span>
            Line {index + 1} of {FRAGMENTS.length}
          </span>
          <span>
            {placed} placed · {records.length - placed} dumped
          </span>
        </div>

        {/* The per-line clock. aria-hidden because a bar that repaints 16
            times a second is noise to a screen reader, not information. */}
        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
          <div
            className="h-full rounded-full bg-indigo-600"
            style={{ width: `${(1 - fragmentProgress) * 100}%` }}
          />
        </div>

        <p
          className="mb-8 min-h-[4.5rem] text-xl font-semibold leading-snug text-slate-900 sm:text-2xl"
          aria-live="polite"
        >
          {fragment.text}
        </p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {ZONES.map((zone) => {
            const tint = ZONE_TINT[zone.id];
            return (
              <button
                key={zone.id}
                type="button"
                onClick={() => advance(zone.id)}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${tint.chip}`}
              >
                <span className={`block text-sm font-bold ${tint.text}`}>
                  {zone.label}
                </span>
                <span className="block text-xs text-slate-600">{zone.hint}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => advance("dump")}
          className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-100"
        >
          Just dump it
        </button>
      </div>
    );
  }

  // ── Handoff: the recall clock waits for a gesture ──────────────
  if (phase === "handoff") {
    const placed = records.filter((r) => r.placement !== "dump").length;
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
        <h2 className="mb-3 text-2xl font-extrabold tracking-tight text-slate-900">
          Meeting over.
        </h2>
        <p className="mb-6 text-slate-600">
          You kept {records.length} of {FRAGMENTS.length} lines — {placed} placed,{" "}
          {records.length - placed} dumped. Now someone asks you five questions
          about it. You get 25 seconds and the notes you took.
        </p>
        <button
          type="button"
          onClick={() => setPhase("recall")}
          className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
        >
          Start the questions
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }

  // ── Recall round ───────────────────────────────────────────────
  if (phase === "recall") {
    const question = QUESTIONS[qIndex];
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-4 flex items-center justify-between text-sm font-medium text-slate-600">
          <span>
            Question {qIndex + 1} of {QUESTIONS.length}
          </span>
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <Clock size={14} aria-hidden="true" />
            {(recallLeft / 1000).toFixed(1)}s left
          </span>
        </div>

        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
          <div
            className="h-full rounded-full bg-indigo-600"
            style={{ width: `${(recallLeft / RECALL_MS) * 100}%` }}
          />
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div>
            <p className="mb-4 text-lg font-bold leading-snug text-slate-900">
              {question.prompt}
            </p>
            <div className="space-y-2">
              {question.options.map((option, i) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => answerQuestion(i)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <NotesPanel records={records} />
        </div>
      </div>
    );
  }

  // ── Result ─────────────────────────────────────────────────────
  const verdict = verdictFor(score);
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
      <h2 className="mb-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
        {verdict.headline}
      </h2>
      <p className="mb-8 max-w-2xl text-slate-600">{verdict.body}</p>

      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        <Meter
          label="Captured"
          value={score.captured}
          max={score.total}
          caption={`${score.placed} placed · ${score.dumped} dumped`}
          barClass="bg-indigo-600"
        />
        <Meter
          label="Found again"
          value={score.recalled}
          max={score.questions}
          caption="questions you could answer from your notes"
          barClass="bg-emerald-600"
        />
      </div>

      <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">
          What the questions were
        </h3>
        <ul className="space-y-2">
          {QUESTIONS.map((q, i) => {
            const right = answers[i] === q.answer;
            return (
              <li key={q.id} className="flex gap-2 text-sm text-slate-700">
                {right ? (
                  <Check size={16} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                ) : (
                  <X size={16} className="mt-0.5 shrink-0 text-rose-600" aria-hidden="true" />
                )}
                <span>
                  <span className="sr-only">{right ? "Correct: " : "Missed: "}</span>
                  {q.prompt}{" "}
                  <strong className="font-semibold text-slate-900">
                    {q.options[q.answer]}
                  </strong>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={copyResult}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Copy size={15} aria-hidden="true" />
          {copied ? "Copied" : "Copy my result"}
        </button>
        <button
          type="button"
          onClick={replay}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
        >
          <RotateCcw size={15} aria-hidden="true" />
          Play again
        </button>
      </div>
    </div>
  );
}

function Meter({
  label,
  value,
  max,
  caption,
  barClass,
}: {
  label: string;
  value: number;
  max: number;
  caption: string;
  barClass: string;
}) {
  const pct = max === 0 ? 0 : (value / max) * 100;
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      <p className="mb-2 text-3xl font-extrabold tabular-nums text-slate-900">
        {value}
        <span className="text-lg font-bold text-slate-600">/{max}</span>
      </p>
      <div className="mb-2 h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-slate-600">{caption}</p>
    </div>
  );
}
